import StopIcon from '@mui/icons-material/Stop';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Alert, Box, Button, CircularProgress, Paper, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  BACKEND_URL,
  streamingHelpers,
  videoStreamingApi,
} from '../../../services/backend/backendApi';
import { useDeviceStore } from '../../../stores/deviceStore';
import { VideoSetupProps } from './VideoSetupProps';

/**
 * @typedef {Object} ShowCamerasProps
 * @property {string} chamberNumber
 * @property {boolean} isStreaming
 * @property {(isStreaming: boolean) => void} setIsStreaming
 * @property {string|null} streamId
 * @property {(id: string|null) => void} setStreamId
 * @property {(url: string) => void} setMjpegUrl
 */

/** @param {ShowCamerasProps} props */
const ShowCamerasButton = ({
  chamberNumber,
  isStreaming,
  setIsStreaming,
  streamId,
  setStreamId,
  setMjpegUrl,
}) => {
  const connectionState = useDeviceStore((state) => state.connectionState);
  const isConnected = connectionState === 'connected';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const ChNumber = useMemo(() => chamberNumber.split('-').pop(), [chamberNumber]);
  const chamberNum = useMemo(() => {
    const num = Number(ChNumber);
    return isNaN(num) ? null : num;
  }, [ChNumber]);
  const isChamber = useMemo(() => ChNumber && ChNumber.length < 3, [ChNumber]);

  const handleShowStream = async () => {
    if (isStreaming) {
      // Stop streaming
      setIsLoading(true);
      setError(null);
      try {
        await videoStreamingApi.stop(streamId ?? undefined);
        setIsStreaming(false);
        setStreamId(null);
        setMjpegUrl('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to stop streaming');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Start streaming
      if (chamberNum === null) {
        setError('Invalid chamber number');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await streamingHelpers.startSync(chamberNum);
        setStreamId(response.stream_id);
        setMjpegUrl(response.mjpeg_url);
        setIsStreaming(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start streaming');
        setIsStreaming(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const buttonText = isStreaming ? 'Stop Stream' : 'Start Stream';
  const buttonIcon = isStreaming ? <StopIcon /> : <VideocamIcon />;

  if (!isChamber) return null;
  if (!isConnected) return null;
  if (!chamberNumber) return null;

  return (
    <Box
      sx={{
        height: '5vh',
        maxHeight: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ width: '100%' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80%',
          width: '100%',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          sx={{ gap: 1, height: '100%' }}
          onClick={handleShowStream}
          disabled={isLoading || !isConnected}
        >
          {isLoading ? <CircularProgress size={16} /> : buttonIcon}
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
};

const VideoStream = ({ mjpegSrc }) => {
  return (
    <img
      src={mjpegSrc}
      alt="Video Stream"
      style={{
        display: 'block',
        width: '95%',
      }}
    />
  );
};

export function StreamVideo({ chamberNumber }) {
  const deviceDescriptor = useDeviceStore((state) => state.deviceDescriptor);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamId, setStreamId] = useState(null);
  const [mjpegUrl, setMjpegUrl] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isStreaming) {
      setHasError(false);
    }
  }, [isStreaming]);

  const mjpegSrc = mjpegUrl ? `${BACKEND_URL}${mjpegUrl}` : '';

  return (
    <Paper
      sx={{
        width: '25%',
        height: '100%',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'top',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        Video Stream
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '50%',
          alignItems: 'center',
          justifyContent: 'top',
          backgroundColor: 'background.default',
          gap: 2,
        }}
      >
        <ShowCamerasButton
          chamberNumber={chamberNumber}
          isStreaming={isStreaming}
          setIsStreaming={setIsStreaming}
          streamId={streamId}
          setStreamId={setStreamId}
          setMjpegUrl={setMjpegUrl}
        />
        {hasError && <Alert severity="error">Stream failed to load</Alert>}
        {isStreaming && mjpegSrc ? (
          <VideoStream mjpegSrc={mjpegSrc} />
        ) : (
          <Skeleton variant="rounded" width="95%" height="70%" animation="pulse" />
        )}
      </Box>
      <VideoSetupProps
        descriptor={deviceDescriptor}
        chamberNumber={chamberNumber}
        streamId={streamId}
        setStreamId={setStreamId}
        setMjpegUrl={setMjpegUrl}
        setIsStreaming={setIsStreaming}
      />
    </Paper>
  );
}

export default ShowCamerasButton;
